// Small presentational parts for the Domain Intelligence drawer.
import {
  Sparkles,
  FileText,
  Target,
  Layers,
  ArrowRight,
  CheckCircle2,
  Network,
} from "lucide-react";
import React from "react";

import {
  CONFIDENCE_RING_SIZE,
  CONFIDENCE_RING_STROKE,
  EMPTY_PLACEHOLDER,
  SCORE_RING_SIZE,
  SCORE_RING_STROKE,
  STAT_TILE_TONE,
} from "../constants";
import { pctValue, strengthLabel, confTone } from "../utils";
import { getDomainIconUrl } from "./utils/assets";
import {
  DOMAIN_PRIMARY_LABELS,
  DOMAIN_SUMMARY_LABELS,
} from "./utils/constants";
import { type DomainIntelDerived } from "./utils/helpers";

// Re-export for consumers that previously imported these from this file.
export {
  EMPTY_PLACEHOLDER,
  UNCOVERED_DOMAIN_CODE,
  NOT_COVERED_STATUS,
} from "../constants";
export { pctValue, strengthLabel, confTone } from "../utils";

export function StatTile({
  value,
  label,
  tone = "cyan",
}: {
  value: string | number;
  label: string;
  tone?: keyof typeof STAT_TILE_TONE;
}) {
  const toneCls = STAT_TILE_TONE[tone];
  return (
    <div className="rounded-lg border border-border bg-card/60 p-3 min-w-0">
      <div className={`text-3xl font-bold ${toneCls} break-words`}>{value}</div>
      <div className="mt-1 text-[11px] tracking-wide text-muted-foreground break-words">
        {label}
      </div>
    </div>
  );
}

export function DomainSummaryOverview({
  derived,
}: {
  derived: DomainIntelDerived;
}) {
  const {
    reqsAnalyzed,
    reqsMapped,
    reqsUnmapped,
    mappedPct,
    multiDomainReqs,
    multiDomainPct,
    totalMappings,
    matchesPerClause,
    coveredCount,
    totalCount,
    overallPct,
  } = derived;

  return (
    <div className="rounded-2xl border border-border/70 bg-gradient-to-br from-cyan-500/[0.06] via-transparent to-indigo-500/[0.06] p-4 sm:p-5">
      {/* Headline: mapped share + progress bar */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-center">
        <div className="min-w-0">
          <div className="flex items-end gap-3">
            <span className="text-5xl font-bold tracking-tight text-cyan-400 tabular-nums leading-none">
              {mappedPct}%
            </span>
            <span className="pb-1 text-xs text-muted-foreground break-words">
              {DOMAIN_SUMMARY_LABELS.mappedHeadlineCaption}
            </span>
          </div>
          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-muted/60"
            role="progressbar"
            aria-valuenow={mappedPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 transition-[width]"
              style={{ width: `${mappedPct}%` }}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-emerald-400" />
              <strong className="text-foreground tabular-nums">
                {reqsMapped}
              </strong>{" "}
              matched
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-muted-foreground/60" />
              <strong className="text-foreground tabular-nums">
                {reqsUnmapped}
              </strong>{" "}
              unmapped
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-muted-foreground/70">of</span>
              <strong className="text-foreground tabular-nums">
                {reqsAnalyzed}
              </strong>{" "}
              total clauses
            </span>
          </div>
        </div>

        {/* Flow: clauses → matched → mappings */}
        <div className="flex items-center justify-between gap-2 rounded-xl border border-border/60 bg-background/40 px-3 py-3 sm:px-4">
          <FlowNode
            icon={<FileText className="size-4 text-cyan-400" />}
            value={reqsAnalyzed}
            label={DOMAIN_SUMMARY_LABELS.flowClauses}
            hint={DOMAIN_SUMMARY_LABELS.flowClausesHint}
          />
          <ArrowRight className="size-4 shrink-0 text-muted-foreground/60" />
          <FlowNode
            icon={<Target className="size-4 text-emerald-400" />}
            value={reqsMapped}
            label={DOMAIN_SUMMARY_LABELS.flowMatched}
            hint={DOMAIN_SUMMARY_LABELS.flowMatchedHint}
          />
          <ArrowRight className="size-4 shrink-0 text-muted-foreground/60" />
          <FlowNode
            icon={<Layers className="size-4 text-indigo-400" />}
            value={totalMappings}
            label={DOMAIN_SUMMARY_LABELS.flowMappings}
            hint={`${matchesPerClause}x per matched clause`}
          />
        </div>
      </div>

      {/* Secondary metrics row */}
      <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <SummaryMetric
          icon={<Network className="size-3.5 text-indigo-400" />}
          value={`${coveredCount} / ${totalCount}`}
          label={DOMAIN_SUMMARY_LABELS.breadthLabel}
          hint={DOMAIN_SUMMARY_LABELS.breadthCaption}
          accent="indigo"
        />
        <SummaryMetric
          icon={<Sparkles className="size-3.5 text-amber-400" />}
          value={multiDomainReqs}
          suffix={multiDomainPct > 0 ? ` · ${multiDomainPct}%` : undefined}
          label={DOMAIN_SUMMARY_LABELS.multiDomain}
          hint={DOMAIN_SUMMARY_LABELS.multiDomainCaption}
          accent="amber"
        />
        <SummaryMetric
          icon={<CheckCircle2 className="size-3.5 text-emerald-400" />}
          value={overallPct}
          label={DOMAIN_SUMMARY_LABELS.overallConfidence}
          hint={DOMAIN_SUMMARY_LABELS.confidenceCaption}
          accent="emerald"
        />
      </div>
    </div>
  );
}

function FlowNode({
  icon,
  value,
  label,
  hint,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  hint?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col items-center text-center">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-xl font-bold text-foreground tabular-nums leading-none">
          {value}
        </span>
      </div>
      <div className="mt-1 text-[10.5px] font-semibold text-foreground break-words">
        {label}
      </div>
      {hint && (
        <div className="mt-0.5 text-[10px] text-muted-foreground break-words">
          {hint}
        </div>
      )}
    </div>
  );
}

const SUMMARY_METRIC_ACCENT = {
  indigo: "border-indigo-500/25 bg-indigo-500/[0.06]",
  amber: "border-amber-500/25 bg-amber-500/[0.06]",
  emerald: "border-emerald-500/25 bg-emerald-500/[0.06]",
} as const;

function SummaryMetric({
  icon,
  value,
  suffix,
  label,
  hint,
  accent,
}: {
  icon: React.ReactNode;
  value: string | number;
  suffix?: string;
  label: string;
  hint?: string;
  accent: keyof typeof SUMMARY_METRIC_ACCENT;
}) {
  return (
    <div
      className={`min-w-0 rounded-lg border px-3 py-2.5 ${SUMMARY_METRIC_ACCENT[accent]}`}
    >
      <div className="flex items-baseline gap-1.5">
        {icon}
        <span className="text-lg font-bold text-foreground tabular-nums leading-none">
          {value}
        </span>
        {suffix && (
          <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
            {suffix}
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] font-semibold text-foreground break-words">
        {label}
      </div>
      {hint && (
        <div className="mt-0.5 text-[10px] text-muted-foreground break-words">
          {hint}
        </div>
      )}
    </div>
  );
}

export function SectionCard({
  headerLabel,
  rightLabel,
  children,
}: {
  headerLabel: string;
  rightLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="size-2 rounded-full bg-teal-400 shadow-[0_0_6px_2px_rgba(45,212,191,0.5)]" />
          <span className="text-xs tracking-wide font-semibold text-foreground break-words">
            {headerLabel}
          </span>
        </div>
        {rightLabel && (
          <span className="text-xs text-muted-foreground break-words">
            {rightLabel}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

export function DomainChip({ code }: { code: string }) {
  const iconUrl = getDomainIconUrl(code);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-violet-500/15 pl-1 pr-2 py-0.5 text-[11px] font-bold tracking-wide text-violet-300 border border-violet-500/25">
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          aria-hidden="true"
          className="size-6 object-contain shrink-0"
          loading="lazy"
        />
      )}
      {code}
    </span>
  );
}

export function StrengthPill({ conf }: { conf?: number | null }) {
  const label = strengthLabel(conf);
  const tone = confTone(conf);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone.pillBg} ${tone.pillText}`}
    >
      {label}
    </span>
  );
}

export function ConfidenceRing({
  value,
  size = CONFIDENCE_RING_SIZE,
  stroke = CONFIDENCE_RING_STROKE,
  caption = DOMAIN_PRIMARY_LABELS.ringCaption,
  textClassName = "text-4xl",
}: {
  value: number | null;
  size?: number;
  stroke?: number;
  /** Defaults to the overview's "Match"; pass "" to move the label outside. */
  caption?: string;
  textClassName?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = value ?? 0;
  const dash = c * pct;
  const tone = confTone(value);
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-border/40"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={tone.ring}
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-1">
        <div className={`${textClassName} font-bold ${tone.text}`}>
          {value === null ? EMPTY_PLACEHOLDER : `${Math.round(pct * 100)}%`}
        </div>
        {caption ? (
          <div className="mt-1 text-center text-[10px] leading-tight tracking-wide text-muted-foreground">
            {caption}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ScoreRing({
  value,
  icon,
  caption,
  size = SCORE_RING_SIZE,
  stroke = SCORE_RING_STROKE,
}: {
  value: string;
  icon?: React.ReactNode;
  caption?: string;
  size?: number;
  stroke?: number;
}) {
  const numeric = Number.parseInt(value.replace("%", ""), 10);
  const pctN = Number.isFinite(numeric) ? numeric / 100 : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * pctN;
  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-border/40"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className="text-emerald-400"
          stroke="currentColor"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {icon}
        <div className="mt-1 text-4xl font-bold text-emerald-400">{value}</div>
        {caption && (
          <div className="mt-0.5 text-[10px] tracking-wide text-muted-foreground">
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

export function MatchBar({ conf }: { conf?: number | null }) {
  const c = pctValue(conf) ?? 0;
  const tone = confTone(conf);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative h-1.5 flex-1 min-w-[60px] rounded-full bg-white/10 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${tone.bar}`}
          style={{ width: `${Math.round(c * 100)}%` }}
        />
      </div>
      <span className={`text-[11px] font-semibold whitespace-nowrap ${tone.text}`}>
        {conf === null || conf === undefined
          ? EMPTY_PLACEHOLDER
          : `${Math.round(c * 100)}%`}
      </span>
    </div>
  );
}

/** Progress bar for a percentage value (0-100). Empty state renders a
 *  placeholder without any bar fill. Bar tone follows the row's confidence
 *  so users can still eyeball match strength at a glance. */
export function SharePctBar({
  share,
  conf,
  empty,
}: {
  share: number | null | undefined;
  conf?: number | null;
  empty: boolean;
}) {
  const tone = confTone(conf);
  const width = empty ? 0 : Math.max(0, Math.min(100, share ?? 0));
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative h-2 flex-1 min-w-[60px] rounded-full border border-border/80 bg-muted/40 overflow-hidden">
        {!empty && (
          <div
            className={`absolute inset-y-0 left-0 ${tone.bar}`}
            style={{ width: `${width}%` }}
          />
        )}
      </div>
      <span className={`text-[11px] font-semibold whitespace-nowrap tabular-nums ${empty ? "text-muted-foreground" : tone.text}`}>
        {empty ? EMPTY_PLACEHOLDER : `${(share ?? 0).toFixed(1)}%`}
      </span>
    </div>
  );
}

export { PrimaryDomainMatch } from "./primaryDomainMatch";
